/**
 * Chart (2.0) — shadcn chart primitives over Recharts, ported to the
 * Athena idiom (JSX + CSS Modules + 2.0 tokens).
 *
 * Composition model is upstream-faithful: you build charts from raw
 * Recharts components and bring in these custom pieces where needed —
 * `ChartContainer` (config context + per-series CSS variables +
 * responsive sizing), `ChartTooltip`/`ChartTooltipContent`, and
 * `ChartLegend`/`ChartLegendContent`. Recharts itself is NOT wrapped.
 *
 * ChartConfig shape (per series key):
 *   { label?: ReactNode, icon?: Component,
 *     color?: string                       // e.g. 'var(--chart-1)' or '#2a78d6'
 *     theme?: { light: string, dark: string } }
 *
 * Colors are exposed to the chart as `--color-<key>` custom properties
 * scoped to the chart container; reference them in Recharts props as
 * fill="var(--color-desktop)". Theme objects map onto Athena's
 * `data-theme` attribute convention (plus the no-attribute FOUC
 * fallback, which mirrors dark) instead of shadcn's `.dark` class.
 *
 * IMPORTANT (upstream note, Recharts v3): ChartContainer must have a
 * height — via className/style or its default aspect-ratio — or
 * ResponsiveContainer cannot measure on first render.
 */
import React from 'react';
import * as RechartsPrimitive from 'recharts';
import styles from './Chart.module.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// Athena theme selector prefixes. Dark also covers the pre-hydration
// FOUC window (no data-theme attribute yet), matching globals.css.
const THEMES = {
  light: [':root[data-theme="light"]'],
  dark: [':root[data-theme="dark"]', ':root:not([data-theme])'],
};

// Exported for tests only (not re-exported from index.js) — lets tooltip
// and legend content render under a bare provider, since jsdom cannot
// measure recharts' ResponsiveContainer.
export const ChartContext = React.createContext(null);

export function useChart() {
  const context = React.useContext(ChartContext);
  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }
  return context;
}

export const ChartContainer = React.forwardRef(
  ({ id, className, children, config, ...props }, ref) => {
    const uniqueId = React.useId();
    const chartId = `chart-${id || uniqueId.replace(/:/g, '')}`;

    return (
      <ChartContext.Provider value={{ config }}>
        <div
          data-slot="chart"
          data-chart={chartId}
          ref={ref}
          className={cx(styles.container, className)}
          {...props}
        >
          <ChartStyle id={chartId} config={config} />
          <RechartsPrimitive.ResponsiveContainer>
            {children}
          </RechartsPrimitive.ResponsiveContainer>
        </div>
      </ChartContext.Provider>
    );
  },
);
ChartContainer.displayName = 'ChartContainer';

export const ChartStyle = ({ id, config }) => {
  const colorConfig = Object.entries(config).filter(
    ([, itemConfig]) => itemConfig.theme || itemConfig.color,
  );

  if (!colorConfig.length) return null;

  const css = Object.entries(THEMES)
    .map(([theme, prefixes]) =>
      prefixes
        .map(
          (prefix) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color = itemConfig.theme?.[theme] || itemConfig.color;
    return color ? `  --color-${key}: ${color};` : null;
  })
  .filter(Boolean)
  .join('\n')}
}
`,
        )
        .join('\n'),
    )
    .join('\n');

  return <style dangerouslySetInnerHTML={{ __html: css }} />;
};

export const ChartTooltip = RechartsPrimitive.Tooltip;

export const ChartTooltipContent = React.forwardRef(
  (
    {
      active,
      payload,
      className,
      indicator = 'dot',
      hideLabel = false,
      hideIndicator = false,
      label,
      labelFormatter,
      labelClassName,
      formatter,
      color,
      nameKey,
      labelKey,
    },
    ref,
  ) => {
    const { config } = useChart();

    const tooltipLabel = React.useMemo(() => {
      if (hideLabel || !payload?.length) return null;

      const [item] = payload;
      const key = `${labelKey || item?.dataKey || item?.name || 'value'}`;
      const itemConfig = getPayloadConfigFromPayload(config, item, key);
      const value =
        !labelKey && typeof label === 'string'
          ? config[label]?.label || label
          : itemConfig?.label;

      if (labelFormatter) {
        return (
          <div className={cx(styles.tooltipLabel, labelClassName)}>
            {labelFormatter(value, payload)}
          </div>
        );
      }
      if (!value) return null;
      return <div className={cx(styles.tooltipLabel, labelClassName)}>{value}</div>;
    }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey]);

    if (!active || !payload?.length) return null;

    const nestLabel = payload.length === 1 && indicator !== 'dot';

    return (
      <div ref={ref} className={cx(styles.tooltip, className)}>
        {!nestLabel ? tooltipLabel : null}
        <div className={styles.tooltipItems}>
          {payload.map((item, index) => {
            const key = `${nameKey || item.name || item.dataKey || 'value'}`;
            const itemConfig = getPayloadConfigFromPayload(config, item, key);
            const indicatorColor = color || item.payload?.fill || item.color;

            return (
              <div
                key={item.dataKey ?? index}
                className={cx(
                  styles.tooltipItem,
                  indicator === 'dot' && styles.tooltipItemCentered,
                )}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, item.payload)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cx(
                            styles.indicator,
                            indicator === 'dot' && styles.indicatorDot,
                            indicator === 'line' && styles.indicatorLine,
                            indicator === 'dashed' && styles.indicatorDashed,
                            nestLabel && indicator === 'dashed' && styles.indicatorDashedNested,
                          )}
                          style={{
                            '--color-bg': indicatorColor,
                            '--color-border': indicatorColor,
                          }}
                        />
                      )
                    )}
                    <div
                      className={cx(
                        styles.tooltipValueRow,
                        nestLabel ? styles.tooltipValueRowNested : styles.tooltipValueRowCentered,
                      )}
                    >
                      <div className={styles.tooltipValueMeta}>
                        {nestLabel ? tooltipLabel : null}
                        <span className={styles.tooltipName}>
                          {itemConfig?.label || item.name}
                        </span>
                      </div>
                      {item.value !== undefined && item.value !== null && (
                        <span className={styles.tooltipValue}>
                          {typeof item.value === 'number'
                            ? item.value.toLocaleString()
                            : item.value}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = 'ChartTooltipContent';

export const ChartLegend = RechartsPrimitive.Legend;

export const ChartLegendContent = React.forwardRef(
  ({ className, hideIcon = false, payload, verticalAlign = 'bottom', nameKey }, ref) => {
    const { config } = useChart();

    if (!payload?.length) return null;

    return (
      <div
        ref={ref}
        className={cx(
          styles.legend,
          verticalAlign === 'top' ? styles.legendTop : styles.legendBottom,
          className,
        )}
      >
        {payload.map((item, index) => {
          const key = `${nameKey || item.dataKey || 'value'}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);

          return (
            <div key={item.value ?? index} className={styles.legendItem}>
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className={styles.legendSwatch}
                  style={{ backgroundColor: item.color }}
                />
              )}
              {itemConfig?.label}
            </div>
          );
        })}
      </div>
    );
  },
);
ChartLegendContent.displayName = 'ChartLegendContent';

/**
 * Resolve the config entry for a tooltip/legend payload item: try the
 * payload's own fields first (item[key] / item.payload[key] naming a
 * config key), then fall back to the key itself.
 */
function getPayloadConfigFromPayload(config, payload, key) {
  if (typeof payload !== 'object' || payload === null) return undefined;

  const payloadPayload =
    'payload' in payload && typeof payload.payload === 'object' && payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey = key;

  if (key in payload && typeof payload[key] === 'string') {
    configLabelKey = payload[key];
  } else if (payloadPayload && key in payloadPayload && typeof payloadPayload[key] === 'string') {
    configLabelKey = payloadPayload[key];
  }

  return configLabelKey in config ? config[configLabelKey] : config[key];
}
